/**
 * @copyright
 * (c) 2026 netTrek GmbH & Co. KG
 *
 * Verification pipeline: per-manifest signature + certificate validity, the
 * active manifest's BMFF/Merkle hard-binding hash (init segment + media
 * fragment), then assembles validation_status / validation_state.
 *
 *  - step 4: COSE_Sign1 verify + signature_info (X.509)        [done]
 *  - step 5: BMFF / Merkle hash verification → validation_state [done]
 *  - step 7: trust-list / CAWG → Trusted + RFC 3161 TSA trust   [done]
 */

import type { WebCryptoReader } from '../reader/Reader'
import type { Settings } from '../createC2pa'
import type { SignatureInfo, ValidationState, ValidationStatus } from '../types'
import { verifyManifestSignature, type ManifestVerification } from './verifyManifest'
import { assertionByLabel, type ParsedManifest } from '../model/parseManifestStore'
import { decodeSign1, x5chain } from '../cose/decodeSign1'
import { parseAllFragmentMerkle, parseMerkleMaps, verifyFragment, verifyInitHash, UnsupportedAlgError, type MerkleMap } from '../bmff/merkle'
import { decode } from '../cbor/decode'
import { computeBmffHash, subsetsAreWellFormed, webcryptoDigestForC2pa, type BmffExclusion } from '../bmff/bmffHash'
import { buildTrustStore, evaluateTrust, type TrustStore } from '../x509/trust'
import { DEFAULT_C2PA_TRUST_ANCHORS, DEFAULT_TRUST_CONFIG } from '../x509/c2paTrustAnchors'
import { validateAssertions } from './validateAssertions'
import { actionIssues, hardBindingCountIssues, ingredientManifestIssues, isHardBinding, redactionIssues, resolveBindingManifest, unverifiedHardBindingCode } from './structuralChecks'
import { validateCawgIdentity } from '../cawg/validateIdentity'
import { parseCertificate } from '../x509/parseCert'
import { checkStapledOcsp, ocspValsFromHeader } from '../x509/verifyOcsp'

/** The C2PA root anchors used for timestamp trust when the caller supplies none. Built once. */
let defaultTimestampStore: TrustStore | null | undefined
function getDefaultTimestampStore (): TrustStore | null {
    if (defaultTimestampStore === undefined) {
        try { defaultTimestampStore = buildTrustStore(DEFAULT_C2PA_TRUST_ANCHORS, DEFAULT_TRUST_CONFIG) } catch { defaultTimestampStore = null }
    }
    return defaultTimestampStore
}

/** Reads a manifest's BMFF hash assertion (v3 then v2), decoded. Exported for tests. */
export function bmffHashAssertion (m: ParsedManifest): { label: string, exclusions: BmffExclusion[], alg?: string, hash?: Uint8Array, maps: MerkleMap[], malformed: boolean } | null {
    const a = assertionByLabel(m, 'c2pa.hash.bmff.v3') ?? assertionByLabel(m, 'c2pa.hash.bmff.v2')
    if (!a) return null
    // A corrupt bmff-hash assertion must degrade to malformed, not throw out of the
    // reader. (decodeAssertion stores it even when its CBOR is broken.)
    let data: Record<string, unknown>
    try {
        data = decode(a.raw) as Record<string, unknown>
    } catch {
        return { label: a.label, exclusions: [], maps: [], malformed: true }
    }
    const exclusions = Array.isArray(data.exclusions) ? data.exclusions as BmffExclusion[] : []
    // BMFF-7: a `merkle` key present but not an array is malformed — do NOT fall
    // through to the monolithic whole-file `hash` path (c2pa-rs errors here).
    const merkleMalformed = data.merkle !== undefined && !Array.isArray(data.merkle)
    return {
        label: a.label,
        exclusions,
        alg: typeof data.alg === 'string' ? data.alg : undefined,
        // The whole-asset content hash for a standalone (non-Merkle) BMFF hard binding.
        hash: data.hash instanceof Uint8Array ? data.hash : undefined,
        maps: parseMerkleMaps(data),
        // §15.12.2: a BMFF-hash assertion must carry a valid (non-empty) exclusions
        // array — it has to exclude at least the C2PA manifest box. Empty/missing →
        // malformed rather than hashing over everything. Also malformed when subset
        // ranges are out-of-order/overlapping/negative (BMFF-2) or `merkle` is
        // present-but-malformed (BMFF-7).
        malformed: exclusions.length === 0 || !subsetsAreWellFormed(exclusions) || merkleMalformed
    }
}

function status (code: string, manifestLabel: string, suffix = 'c2pa.signature'): ValidationStatus {
    return { code, url: `self#jumbf=/c2pa/${manifestLabel}/${suffix}`, explanation: code }
}

/** Trust resources arrive as inline text (the bridge fetches URLs to text); join arrays. */
function asText (v: string | string[] | undefined): string {
    if (Array.isArray(v)) return v.join('\n')
    return v ?? ''
}

export async function runVerification (reader: WebCryptoReader, settings: Settings | undefined): Promise<void> {
    const parsed = reader.parsed
    const nowIso = new Date().toISOString()

    // Build the caller's trust store when trust resources are supplied. It backs
    // signer trust (gated by verifyTrust below) and, when present, timestamp trust.
    let trustStore: TrustStore | null = null
    if (settings?.trust) {
        try {
            trustStore = buildTrustStore(
                asText(settings.trust.trustAnchors),
                asText(settings.trust.trustConfig),
                asText(settings.trust.allowedList)
            )
        } catch { trustStore = null }
    }

    // CAWG identity trust: the WASM reference was switched to a fork (c2pa-rs
    // "cawg-untrusted-differentiation", issue #2247) that reports a present-but-untrusted
    // CAWG identity with the DIFFERENTIATED code `cawg.identity.untrusted` on the identity
    // URL — instead of the generic `signingCredential.untrusted`. The state behavior is
    // unchanged from the reference: a present, untrusted CAWG identity is GATING (caps the
    // manifest at Valid, never Trusted). We mirror that in the CAWG block below.

    // Timestamp trust uses the caller's anchors when supplied (trust-on, which may
    // include intermediate TSA CAs), else the bundled C2PA roots (trust-off). This
    // mirrors c2pa-rs honoring the RFC 3161 timestamp only for a trusted TSA.
    const timestampStore = trustStore ?? getDefaultTimestampStore()

    const verifications: ManifestVerification[] = []
    for (const m of parsed.manifests) {
        verifications.push(await verifyManifestSignature(m, nowIso, {
            timestampStore,
            requireTrustedTimestamp: m.label === parsed.activeLabel
        }))
    }
    reader.verifications = verifications

    const signatureInfos = new Map<string, SignatureInfo>()
    for (const v of verifications) {
        if (v.signatureInfo) signatureInfos.set(v.label, v.signatureInfo)
    }

    // Hard-binding hash of the active manifest (init segment + media fragment).
    let hashValid = true
    let hashChecked = false
    let hashFailCode = 'assertion.bmffHash.mismatch'
    let hashFailSuffix = 'c2pa.assertions/c2pa.hash.bmff.v3'
    const active = parsed.activeLabel ? parsed.byLabel.get(parsed.activeLabel) : undefined
    // The manifest whose hard binding governs the asset content (§15.12): the active
    // manifest for a standard manifest, else the standard manifest reached by walking
    // the `parentOf` ingredient chain from an update-manifest-active asset. `null` for
    // an update-active asset with a broken/absent parent chain (STATE-1).
    const bindingManifest = active
        ? (active.isUpdateManifest ? resolveBindingManifest(active, parsed) : active)
        : undefined
    if (active && active.isUpdateManifest && !bindingManifest) {
        // STATE-1: an update manifest is active but no standard manifest binds the
        // content → the claim is never content-bound. Reject rather than pass.
        hashChecked = true
        hashValid = false
        hashFailCode = 'claim.hardBindings.missing'
        hashFailSuffix = 'c2pa.claim'
    } else if (bindingManifest) {
        const bmff = bmffHashAssertion(bindingManifest)
        if (bmff) {
            hashFailSuffix = `c2pa.assertions/${bmff.label}`
            if (bmff.malformed) {
                // D (§15.12.2): no valid exclusions array → malformed hard binding.
                hashChecked = true
                hashValid = false
                hashFailCode = 'assertion.bmffHash.malformed'
            } else if (bmff.maps.length > 0) {
                hashChecked = true
                const fragment = reader.input.fragment
                if (fragment) {
                    // Verify EVERY per-track Merkle box in the fragment (BMFF-1), plus the
                    // init-segment hash for each track's rendition. The hash alg is
                    // resolved strictly (§15.4): map → assertion → claim alg; an
                    // unsupported/absent alg throws → algorithm.unsupported.
                    try {
                        const fms = parseAllFragmentMerkle(fragment)
                        if (!fms || fms.length === 0) {
                            // No parseable per-fragment Merkle box → content unconfirmable.
                            hashValid = false
                        } else {
                            let ok = true
                            for (const fm of fms) {
                                const map = bmff.maps.find(mm => mm.uniqueId === fm.uniqueId && mm.localId === fm.localId)
                                if (!map) { ok = false; break } // fragment box with no matching map (BMFF-1)
                                if (!map.initHash) {
                                    // §18.6.3: a fragmented rendition's MerkleMap MUST carry an
                                    // initHash — its absence is malformed, not a silent pass.
                                    hashValid = false
                                    hashFailCode = 'assertion.bmffHash.malformed'
                                    ok = false
                                    break
                                }
                                if (!await verifyInitHash(reader.input.init, map, bmff.exclusions, bmff.alg, bindingManifest.claim.alg)) { ok = false }
                            }
                            hashValid = ok && await verifyFragment(fragment, bmff.maps, bmff.exclusions, bmff.alg, bindingManifest.claim.alg)
                        }
                    } catch (e) {
                        if (e instanceof UnsupportedAlgError) { hashValid = false; hashFailCode = 'algorithm.unsupported' }
                        else throw e
                    }
                } else {
                    // No fragment: the fragmented hard binding can't be confirmed (matches
                    // the WASM lib treating init-only reads as not-valid).
                    hashValid = false
                }
            } else {
                // Standalone (non-Merkle) whole-file BMFF hash (§18.6.2): the assertion's
                // top-level `hash` covers the entire asset minus its exclusions. Verify it
                // against the whole asset (`reader.input.init` holds the fromBlob bytes).
                hashChecked = true
                const digest = webcryptoDigestForC2pa(bmff.alg ?? bindingManifest.claim.alg)
                if (!digest) {
                    hashValid = false
                    hashFailCode = 'algorithm.unsupported'
                } else if (!bmff.hash) {
                    // A BMFF hard binding with neither a Merkle tree nor a whole-asset hash
                    // is malformed.
                    hashValid = false
                    hashFailCode = 'assertion.bmffHash.malformed'
                } else {
                    const computed = await computeBmffHash(reader.input.init, { exclusions: bmff.exclusions, alg: digest, bmffV2: true })
                    hashValid = computed.length === bmff.hash.length && computed.every((b, i) => b === bmff.hash![i])
                }
            }
        } else {
            // C (§15.12 / §15.5.5): the binding manifest's hard binding is a non-BMFF
            // type this fragmented-BMFF reader does not verify. An unconfirmable hard
            // binding is treated as non-matching rather than silently reported Valid.
            // (A binding manifest with *no* hard binding at all is caught by the
            // hard-binding-count check on the binding manifest below.)
            const hb = bindingManifest.assertions.find(a => isHardBinding(a.label))
            const code = hb ? unverifiedHardBindingCode(hb.label) : null
            if (code && hb) {
                hashChecked = true
                hashValid = false
                hashFailCode = code
                hashFailSuffix = `c2pa.assertions/${hb.label}`
            }
        }
    }

    // Trust evaluation (X.509 chain → anchor, or allow-listed end-entity). Only
    // when trust verification is requested and trust resources are supplied.
    let activeTrusted = false
    const activeVerif = verifications.find(v => v.label === parsed.activeLabel)
    if ((settings?.verify?.verifyTrust ?? false) && trustStore && activeVerif?.chain.length && activeVerif.sign1) {
        try {
            // Evaluate the signer chain at the SAME gated reference time used for
            // expiry (a trusted-timestamp genTime, else now). Using the raw,
            // unverified genTime here would let a forged timestamp validate an
            // otherwise-expired chain link.
            activeTrusted = (await evaluateTrust(activeVerif.chain, trustStore, activeVerif.refTime)).trusted
        } catch { /* leave untrusted on any trust-eval error */ }
    }

    // CAWG identity trust — mirrors the fixed WASM fork (c2pa-rs
    // "cawg-untrusted-differentiation", issue #2247, commit "use caller's Context settings
    // for X.509 identity trust"). A present `cawg.x509.cose` identity is trust-evaluated
    // against the caller's `settings.cawgTrust` (its own anchors/config/allow-list),
    // INDEPENDENTLY of the claim signer, and reported on the identity URL with CAWG codes
    // (never `signingCredential.*`):
    //   • trusted   → `cawg.identity.trusted` (SUCCESS; NON-GATING — allows Trusted).
    //   • untrusted → `cawg.identity.untrusted` (FAILURE; GATES → caps state at Valid).
    // Success codes live in validation_results (v2); the flat validation_status carries
    // only the untrusted failure (as signingCredential.trusted is likewise v2-only). So a
    // trusted identity emits nothing here and does not gate; an untrusted one does both.
    // Validate the CAWG identity assertion (CAWG Identity §7–§8): the identity COSE
    // signature over its signer_payload, the referenced_assertions binding, and
    // sig_type/CBOR diagnostics. All non-gating for validation_state. A
    // signature-invalid X.509 identity has its reported signature_info withheld
    // (below, via mapOptions). Trust is evaluated only after a valid signature —
    // matching the fork, which withholds the trusted/untrusted verdict on a
    // signature failure.
    const cawgWithhold = new Set<string>()
    const cawgCodes: string[] = []
    const cawgPresent = new Set<string>()
    let cawgIdentityUntrusted = false
    for (const m of parsed.manifests) {
        const idv = await validateCawgIdentity(m)
        if (!idv.present) continue
        cawgPresent.add(m.label)
        // Withhold the X.509 identity summary when its COSE signature is invalid.
        if (idv.signatureValid === false && idv.sigType === 'cawg.x509.cose') cawgWithhold.add(m.label)
        if (m.label !== parsed.activeLabel) continue
        cawgCodes.push(...idv.codes)
        // Trust gating (fork parity): only when trust is requested, the identity is
        // an X.509 identity with a valid signature, and it is not on the caller's
        // cawgTrust list. A signature-invalid identity emits no trusted/untrusted.
        if ((settings?.verify?.verifyTrust ?? false) && idv.sigType === 'cawg.x509.cose' && idv.signatureValid === true) {
            const cawg = assertionByLabel(m, 'cawg.identity')?.decoded as { signature?: unknown } | undefined
            if (cawg?.signature instanceof Uint8Array) {
                try {
                    const chain = x5chain(decodeSign1(cawg.signature))
                    if (chain.length > 0) {
                        let cawgTrustStore: TrustStore | null = null
                        if (settings?.cawgTrust) {
                            try {
                                cawgTrustStore = buildTrustStore(
                                    asText(settings.cawgTrust.trustAnchors),
                                    asText(settings.cawgTrust.trustConfig),
                                    asText(settings.cawgTrust.allowedList))
                            } catch { cawgTrustStore = null }
                        }
                        const cawgTrusted = cawgTrustStore
                            ? (await evaluateTrust(chain, cawgTrustStore, activeVerif?.refTime ?? nowIso)).trusted
                            : false
                        cawgIdentityUntrusted = !cawgTrusted
                    }
                } catch { /* leave non-gating on eval error */ }
            }
        }
    }

    // Stapled OCSP revocation (§15.9.1): if the active signer's COSE `rVals` header
    // staples an OCSP response that marks the signer (or an intermediate) revoked —
    // signed by an authorized responder, within its validity window relative to the
    // attested time-stamp — the claim is rejected. `good`/`unknown`/absent → not
    // revoked (§15.9 default). Online OCSP (§15.9.2) is not performed. No-op for
    // fixtures carrying only `good` responses (recon-gated).
    let ocspRevoked = false
    if (active && activeVerif?.sign1 && activeVerif.chain.length >= 2) {
        const ocspDers = ocspValsFromHeader(activeVerif.sign1.unprotectedHeader.get('rVals'))
        if (ocspDers.length > 0) {
            try {
                const chain = activeVerif.chain.map(der => parseCertificate(der))
                ocspRevoked = (await checkStapledOcsp(ocspDers, chain, activeVerif.attestedTime, nowIso)) === 'revoked'
            } catch { ocspRevoked = false }
        }
    }

    // Assemble validation status + state.
    const validationStatus: ValidationStatus[] = []
    let hardFail = false

    for (const v of verifications) {
        if (!v.cert) continue
        if (v.signatureValid === false) {
            validationStatus.push(status('claimSignature.mismatch', v.label))
            hardFail = true
        }
        // c2pa-rs emits signingCredential.expired for any validity failure — both
        // expired and not-yet-valid (is_valid_at); there is no separate code.
        if (v.expired || v.notYetValid) {
            validationStatus.push(status('signingCredential.expired', v.label))
            hardFail = true
        }
    }

    // The active manifest must carry a validated claim signature. c2pa-rs requires
    // claimSignature.validated for a Valid/Trusted state; a missing/unparseable
    // signing certificate (no positive signature result) is a hard failure, not a
    // silent pass.
    if (active && !activeVerif?.cert) {
        validationStatus.push(status('claimSignature.missing', active.label))
        hardFail = true
    }

    // C2PA end-entity certificate-profile enforcement (§14.5.1.1): a crypto-valid but
    // profile-violating signing cert is rejected with signingCredential.invalid,
    // independent of trust. Scoped to the active manifest (c2pa-rs runs it on the
    // verified leaf); every conformant fixture signer passes (recon-gated).
    if (active && activeVerif?.cert && !activeVerif.profileValid) {
        validationStatus.push(status('signingCredential.invalid', active.label))
        hardFail = true
    }

    // Stapled-OCSP revocation of the active signer (§15.9.1) → reject.
    if (ocspRevoked && active) {
        validationStatus.push(status('signingCredential.ocsp.revoked', active.label))
        hardFail = true
    }

    if (hashChecked && !hashValid) {
        hardFail = true
        // Report under the binding manifest (the parent for an update-active asset),
        // falling back to the active manifest's own claim when the parent is unresolved.
        const bindingLabel = (bindingManifest ?? active)?.label
        if (bindingLabel) validationStatus.push(status(hashFailCode, bindingLabel, hashFailSuffix))
    }

    // Assertion validation (§15.10): recompute each claim-referenced assertion's
    // hashed-URI hash (§8.4.2.3) and enforce declared/resolvable/well-formed +
    // redaction rules for every manifest (c2pa-rs validates assertions for all
    // manifests in the validation set). A failure is a hard fail. No-op for clean
    // manifests — the sole tamper-detection gate for assertions other than the
    // BMFF hard binding.
    for (const m of parsed.manifests) {
        const issues = await validateAssertions(m, parsed.buffer)
        for (const issue of issues) {
            validationStatus.push(status(issue.code, m.label, `c2pa.assertions/${issue.label}`))
            hardFail = true
        }
    }

    // Structural hard-fails (§15.10.1 / §15.11): the binding (active) manifest must
    // carry exactly one hard binding (an update manifest none), and every ingredient
    // manifest hashed-URI must match the referenced manifest's box hash. No-ops for
    // clean fixtures (each manifest has exactly one hard binding + valid ingredient hashes).
    const structuralIssues = []
    if (active) structuralIssues.push(...hardBindingCountIssues(active))
    // STATE-1: for an update-manifest-active asset, the resolved standard parent must
    // itself carry exactly one hard binding (zero → claim.hardBindings.missing).
    if (bindingManifest && bindingManifest !== active) structuralIssues.push(...hardBindingCountIssues(bindingManifest))
    const redactedUris = parsed.manifests.flatMap(m => m.claim.redactedAssertions)
    for (const m of parsed.manifests) {
        structuralIssues.push(...await ingredientManifestIssues(m, parsed, redactedUris))
        // §15.11.3.3.1 (STATE-3): a redacted assertion still present must be zeroed.
        structuralIssues.push(...redactionIssues(m, parsed))
        // §18.15.4.5: a c2pa.created action must record a digitalSourceType.
        structuralIssues.push(...actionIssues(m))
    }
    for (const issue of structuralIssues) {
        validationStatus.push(status(issue.code, issue.manifestLabel, issue.suffix))
        hardFail = true
    }

    // Untrusted signer → informational status (not a hard failure). c2pa-rs reports
    // this per signer relative to the requested trust policy (§14 flat-array
    // parity):
    //   • trust OFF (no trust list) → every manifest's signer is untrusted, and a
    //     present CAWG identity is likewise `signingCredential.untrusted` on its URL
    //     (stock, non-differentiated).
    //   • trust ON → only the active signer's trust is evaluated here; it is untrusted
    //     when it does not anchor / isn't allow-listed. (A present untrusted CAWG
    //     identity is reported below with the differentiated `cawg.identity.untrusted`.)
    const trustVerified = settings?.verify?.verifyTrust ?? false
    if (!trustVerified) {
        for (const v of verifications) {
            if (v.cert) validationStatus.push(status('signingCredential.untrusted', v.label))
        }
        if (active && cawgPresent.has(active.label)) {
            validationStatus.push(status('signingCredential.untrusted', active.label, 'c2pa.assertions/cawg.identity'))
        }
    } else if (!activeTrusted && active) {
        validationStatus.push(status('signingCredential.untrusted', active.label))
    }
    // A present, untrusted CAWG identity is reported with the differentiated CAWG code on
    // the identity URL (not `signingCredential.*`) and gates the state below (fork parity).
    if (cawgIdentityUntrusted && active) {
        validationStatus.push(status('cawg.identity.untrusted', active.label, 'c2pa.assertions/cawg.identity'))
    }
    // CAWG identity binding / diagnostic failures (CAWG Identity §7) — reported on the
    // identity URL, non-gating (they do not set hardFail). No-op for clean fixtures.
    for (const code of cawgCodes) {
        if (active) validationStatus.push(status(code, active.label, 'c2pa.assertions/cawg.identity'))
    }

    // Trusted requires the claim signer trusted AND no untrusted CAWG identity present
    // (a present untrusted identity caps the state at Valid — fork reference behavior). An
    // untrusted credential is not a hard fail, so it lands at Valid.
    //
    // LOAD-BEARING INVARIANT (STATE-4): the verdict is "Invalid iff some check set
    // hardFail" — i.e. Valid is derived from the ABSENCE of failure, not the presence of
    // positive success codes. Every new integrity/soundness failure code added above MUST
    // set `hardFail = true` (the only deliberately non-gating codes are the CAWG
    // binding/diagnostic codes and `signingCredential.untrusted`/`cawg.identity.untrusted`,
    // which gate trust, not validity). A computed-but-not-gated failure would silently
    // default to Valid. When adding a check, wire hardFail; do not rely on reporting alone.
    const validationState: ValidationState = hardFail ? 'Invalid' : ((activeTrusted && !cawgIdentityUntrusted) ? 'Trusted' : 'Valid')

    reader.setMapOptions({ signatureInfos, validationStatus, validationState, cawgWithholdSignatureInfo: cawgWithhold })
}
